import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Icon from 'components/Icon'
import {
  getBackgroundColorFromReliabilityScore,
  getServiceNotInTeamTooltipMessage
} from 'components/reliability-management/company-dashboard/rm-company-dashboard-utils'
import { formatOwnerDetails } from 'components/services/service-utils'
import useAuth from 'hooks/use-auth'
import { useGetService } from 'hooks/use-services'
import theme from 'theme'
import {
  ServiceReportResponse,
  ServiceReportTrendType
} from 'types/api/policies/reliability-report'
import { formatDateTime } from 'utils/formatDate'

function getTrendVariations(trendType: ServiceReportTrendType) {
  switch (trendType) {
    case 'Up':
      return {
        trendColor: 'success.main',
        trendIcon: 'solidArrowTrendUp',
        trendIndicator: '+'
      }
    case 'Down':
      return {
        trendColor: 'error.main',
        trendIcon: 'solidArrowTrendDown',
        trendIndicator: '-'
      }
    default:
      return {
        trendColor: 'text.primary',
        trendIcon: 'solidDash',
        trendIndicator: ''
      }
  }
}

export const DashboardSummaryCard: React.FC<{
  service: Partial<ServiceReportResponse>
}> = ({ service, ...rest }) => {
  const { currentTeam, currentTeamId } = useAuth()
  const { trendIcon, trendColor, trendIndicator } = getTrendVariations(
    service.trendType
  )
  const userDefinedService = useGetService(service.serviceId)?.data

  const isInCurrentTeam = userDefinedService?.teamId === currentTeamId

  const tooltipMessage =
    !isInCurrentTeam && getServiceNotInTeamTooltipMessage(currentTeam.name)

  const { serviceOwner, teamOwner } = service
  const { teamName, teamOwnerName, teamOwnerEmail } = teamOwner

  const hasServiceOwner = Boolean(serviceOwner)

  return (
    <Tooltip arrow placement="top" title={tooltipMessage}>
      <Card
        data-test-company-dashboard-summary-card
        sx={(muiTheme) => ({
          backgroundColor: getBackgroundColorFromReliabilityScore(
            muiTheme,
            service.score
          )
        })}
        {...rest}
      >
        <CardActionArea
          component={RouterLink}
          data-test-company-dashboard-summary-card-action={service.serviceId}
          disabled={!isInCurrentTeam}
          to={`services/${service.serviceId}`}
        >
          <CardContent>
            <Stack direction="column" gap={1}>
              <Tooltip arrow placement="top" title={service.serviceName}>
                <Typography
                  data-test-service-name
                  noWrap
                  css={{ textOverflow: 'ellipsis' }}
                  variant="h5"
                >
                  {service.serviceName}
                </Typography>
              </Tooltip>
              <Stack alignItems="center" direction="row" gap={2}>
                <Typography variant="h4">{service.score}</Typography>
                <Typography
                  color={trendColor}
                  data-test-trend-type={service.trendType}
                  variant="body1"
                >
                  {`${trendIndicator}${service.delta}`}
                  <Icon css={{ marginLeft: theme.space[1] }} icon={trendIcon} />
                </Typography>
              </Stack>

              <Stack direction="column" gap={0}>
                <Typography data-test-last-modified>
                  Last modified: {formatDateTime(service.lastModified)}
                </Typography>
                {hasServiceOwner ? (
                  <Typography data-test-service-owner-information>
                    {formatOwnerDetails(
                      serviceOwner.identifier,
                      serviceOwner.firstName,
                      serviceOwner.lastName
                    )}
                  </Typography>
                ) : (
                  <>
                    <Typography data-test-team-name>{teamName}</Typography>
                    <Typography>
                      <span data-test-team-owner>{teamOwnerName}</span> (
                      <span data-test-team-owner-email>{teamOwnerEmail}</span>)
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  )
}
